'use client';

import {
  useCommendaOSEntitySchema,
  useCommendaOSSchema,
} from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Button,
  Dropdown,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { AlertCircle, Info, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import { SchemaFieldPicker } from '../../editor/SchemaFieldPicker';
import type { FormComponent, ValidationRules } from '@commenda/types';

interface UnifiedFieldOption {
  entity: string;
  readPath: string;
  label: string;
  group: string;
  filters?: { key: string; value: string }[];
  fieldTypeInfo?: {
    type: string;
    parsingType?: string;
    updatePath?: string;
    createPath?: string;
  };
}

const entityToCategoryLabel = (entity: string): string => {
  if (entity === 'keyPerson') return 'Person';
  if (entity === 'keyPersonRelationship') return 'Relationship';
  if (entity === 'directorDocument') return 'Document';
  return entity;
};

export function EntitySelectOrCreateEditor({
  props,
  template,
  onUpdateProps,
  onUpdateTemplate,
  onUpdateLabel,
  onUpdateValidation,
  currentLabel,
  currentValidation,
}: {
  props: Record<string, unknown>;
  template?: FormComponent[];
  onUpdateProps: (key: string, value: unknown) => void;
  onUpdateTemplate: (template: FormComponent[]) => void;
  onUpdateLabel?: (label: string) => void;
  onUpdateValidation?: (validation: FormComponent['validation']) => void;
  currentLabel?: string;
  currentValidation?: FormComponent['validation'];
}) {
  const { entities, isLoading: isSchemaLoading } = useCommendaOSSchema();

  const listableEntities = useMemo(() => {
    if (!entities) return [];
    return Object.values(entities)
      .filter(
        (entity) =>
          entity.listConfig?.enabled && entity.name !== 'keyPersonRelationship'
      )
      .map((entity) => ({
        name: entity.name,
        label: entity.label,
        listPath: entity.listConfig?.path,
      }));
  }, [entities]);

  const selectedEntity = (props?.entity as string) || 'corporation';
  const selectedEntitySchema = entities?.[selectedEntity];
  const { entitySchema, isLoading: isEntitySchemaLoading } =
    useCommendaOSEntitySchema(selectedEntity);

  const isKeyPersonEntity = selectedEntity === 'keyPerson';
  const { entitySchema: keyPersonRelationshipSchema } =
    useCommendaOSEntitySchema(isKeyPersonEntity ? 'keyPersonRelationship' : '');
  const { entitySchema: directorDocumentSchema } = useCommendaOSEntitySchema(
    isKeyPersonEntity ? 'directorDocument' : ''
  );

  useEffect(() => {
    if (!entities || !selectedEntity || !onUpdateLabel) return;

    const entity = entities[selectedEntity];
    if (!entity) return;

    const isDefaultLabel =
      !currentLabel ||
      currentLabel === 'Select Entity' ||
      currentLabel === 'New Select Entity';

    if (isDefaultLabel) {
      onUpdateLabel(`Add ${entity.label}(s)`);
    }
  }, [entities, selectedEntity, currentLabel, onUpdateLabel]);

  const handleEntityChange = (entityName: string) => {
    const entity = entities?.[entityName];
    onUpdateProps('entity', entityName);
    if (entity?.listConfig?.path) {
      onUpdateProps('listPath', entity.listConfig.path);
    }
    onUpdateProps('displayField', '');
    onUpdateProps('valueField', 'id');

    if (onUpdateLabel && entity) {
      onUpdateLabel(`Add ${entity.label}(s)`);
    }

    onUpdateTemplate([]);
  };

  const requiredFields = useMemo(() => {
    if (!entitySchema?.fields) return [];

    const fields: Array<{ path: string; label: string; type: string }> = [];

    const findRequiredFields = (
      schemaFields: Record<string, unknown>,
      parentPath = ''
    ) => {
      Object.entries(schemaFields).forEach(([key, field]) => {
        const f = field as Record<string, unknown>;
        const readConfig = f.read as { path?: string } | undefined;
        const createConfig = f.create as
          | { path?: string; required?: boolean }
          | undefined;

        if (createConfig?.required && readConfig?.path) {
          fields.push({
            path: readConfig.path,
            label: (f.label as string) || key,
            type: (f.type as string) || 'string',
          });
        }

        if (f.type === 'object' && f.fields) {
          findRequiredFields(
            f.fields as Record<string, unknown>,
            readConfig?.path || parentPath
          );
        }
      });
    };

    findRequiredFields(entitySchema.fields);
    return fields;
  }, [entitySchema]);

  const unifiedFieldOptions = useMemo((): UnifiedFieldOption[] => {
    if (!isKeyPersonEntity) return [];
    const options: UnifiedFieldOption[] = [];

    if (entitySchema?.fields) {
      const addFields = (fields: Record<string, unknown>, parentPath = '') => {
        Object.entries(fields).forEach(([key, field]) => {
          const f = field as Record<string, unknown>;
          const readConfig = f.read as
            | { path?: string; computed?: boolean }
            | undefined;
          if (f.type === 'object' && f.fields) {
            addFields(
              f.fields as Record<string, unknown>,
              readConfig?.path || parentPath
            );
            return;
          }
          if (!readConfig?.path || readConfig.computed) return;
          if (
            key === 'id' ||
            key === 'stakeholderId' ||
            key === 'kycIds' ||
            key === 'locations'
          )
            return;
          options.push({
            entity: 'keyPerson',
            readPath: readConfig.path,
            label: (f.label as string) || key,
            group: 'Person',
            fieldTypeInfo: {
              type: (f.type as string) || 'string',
              parsingType: f.parsingType as string | undefined,
              updatePath: (f.update as { path?: string })?.path,
              createPath: (f.create as { path?: string })?.path,
            },
          });
        });
      };
      addFields(entitySchema.fields);
    }

    if (keyPersonRelationshipSchema?.fields) {
      Object.entries(keyPersonRelationshipSchema.fields).forEach(
        ([key, field]) => {
          const f = field as unknown as Record<string, unknown>;
          const readConfig = f.read as { path?: string } | undefined;
          if (!readConfig?.path) return;
          if (key === 'directorId' || key === 'corporationId') return;
          options.push({
            entity: 'keyPersonRelationship',
            readPath: readConfig.path,
            label: (f.label as string) || key,
            group: 'Relationship',
            fieldTypeInfo: {
              type: (f.type as string) || 'string',
              parsingType: f.parsingType as string | undefined,
              updatePath: (f.update as { path?: string })?.path,
              createPath: (f.create as { path?: string })?.path,
            },
          });
        }
      );
    }

    if (directorDocumentSchema) {
      const docKindFilter = directorDocumentSchema.filters as
        | Record<
            string,
            { enumOptions?: Array<{ value: string; label: string }> }
          >
        | undefined;
      const docKinds = docKindFilter?.documentKind?.enumOptions || [
        { value: 'governmentId', label: 'Government ID' },
        { value: 'panCard', label: 'Pan Card' },
        { value: 'aadharCard', label: 'Aadhar Card' },
        { value: 'addressProof', label: 'Address Proof' },
        { value: 'dscProof', label: 'DSC Proof' },
        { value: 'bankTransferProof', label: 'Bank Transfer Proof' },
      ];
      docKinds.forEach((kind) => {
        options.push({
          entity: 'directorDocument',
          readPath: 'fileUrl',
          label: `${kind.label}`,
          group: 'Documents',
          filters: [{ key: 'documentKind', value: kind.value }],
          fieldTypeInfo: { type: 'string' },
        });
      });
    }

    return options;
  }, [
    isKeyPersonEntity,
    entitySchema,
    keyPersonRelationshipSchema,
    directorDocumentSchema,
  ]);

  const groupedUnifiedOptions = useMemo(() => {
    const groups = new Map<string, UnifiedFieldOption[]>();
    unifiedFieldOptions.forEach((opt) => {
      const list = groups.get(opt.group) ?? [];
      list.push(opt);
      groups.set(opt.group, list);
    });
    return groups;
  }, [unifiedFieldOptions]);

  const handleAddTemplateField = (option?: UnifiedFieldOption) => {
    const entity = option?.entity || selectedEntity;
    const readPath = option?.readPath || '';
    const label = option?.label || 'New Field';
    const filters = option?.filters;

    const newField: FormComponent = {
      id: `template-field-${Date.now()}`,
      component: 'AutoDetectField',
      fieldId: readPath
        ? `${entity}-${readPath.replace(/\./g, '-')}${filters ? `-${filters.map((f) => f.value).join('-')}` : ''}`
        : `field-${Date.now()}`,
      label,
      order: template?.length || 0,
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity,
          field: { read: readPath },
          mode: 'read-write',
          contextBindings: [],
          ...(filters ? { filters } : {}),
        },
      },
    };

    onUpdateTemplate([...(template || []), newField]);
  };

  const unifiedFieldMenu = useMemo(() => {
    const groupOrder = ['Person', 'Relationship', 'Documents'];
    return {
      groups: groupOrder
        .filter((name) => groupedUnifiedOptions.has(name))
        .map((groupName) => ({
          label: groupName,
          items: (groupedUnifiedOptions.get(groupName) ?? []).map((opt) => ({
            label: opt.label,
            onClick: () => handleAddTemplateField(opt),
          })),
        })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedUnifiedOptions]);

  const handleAutoPopulateRequiredFields = () => {
    if (requiredFields.length === 0) return;

    const newTemplateFields: FormComponent[] = requiredFields.map(
      (field, index) => ({
        id: `template-field-${Date.now()}-${index}`,
        component: 'AutoDetectField',
        fieldId: field.path.replace(/\./g, '-'),
        label: field.label,
        order: index,
        dataSource: {
          type: COMMENDA_OS_DATA_SOURCE,
          commendaOS: {
            entity: selectedEntity,
            field: { read: field.path },
            mode: 'read-write',
            contextBindings: [],
          },
        },
      })
    );

    onUpdateTemplate(newTemplateFields);
  };

  const handleRemoveTemplateField = (fieldId: string) => {
    onUpdateTemplate((template || []).filter((f) => f.id !== fieldId));
  };

  const handleUpdateTemplateField = (
    fieldId: string,
    updates: Partial<FormComponent>
  ) => {
    onUpdateTemplate(
      (template || []).map((f) => (f.id === fieldId ? { ...f, ...updates } : f))
    );
  };

  // Rebuilds validation rules whenever min/max selection changes. Shared between
  // the two number inputs below — `which` flags which input the user touched so
  // the *other* side reads from props (not the stale state pre-update).
  const updateSelectionValidation = (which: 'min' | 'max', value: number) => {
    if (!onUpdateValidation) return;
    const minValue =
      which === 'min' ? value : ((props?.minSelection as number) ?? 1);
    const maxValue =
      which === 'max' ? value : ((props?.maxSelection as number) ?? 1);
    const entityLabel = selectedEntitySchema?.label || 'entity';
    const rules: ValidationRules = { ...currentValidation?.rules };

    if (minValue > 0) {
      rules.required = {
        enabled: true,
        message: `Please select at least ${minValue} ${entityLabel}${minValue > 1 ? 's' : ''}`,
      };
      rules.minLength = {
        enabled: true,
        value: minValue,
        message: `Please select at least ${minValue} ${entityLabel}${minValue > 1 ? 's' : ''}`,
      };
    } else {
      delete rules.required;
      delete rules.minLength;
    }

    if (maxValue > 0) {
      rules.maxLength = {
        enabled: true,
        value: maxValue,
        message: `Please select no more than ${maxValue} ${entityLabel}${maxValue > 1 ? 's' : ''}`,
      };
    } else {
      delete rules.maxLength;
    }

    onUpdateValidation({ rules });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Entity Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Entity Selection */}
        <div className="space-y-2">
          <Label>Entity Type</Label>
          {isSchemaLoading ? (
            <div className="flex items-center gap-2 p-2 border rounded-md">
              <LoadingIndicator size={16} />
              <span className="txt-small-regular text-text-tertiary">
                Loading entities...
              </span>
            </div>
          ) : (
            <Select value={selectedEntity} onValueChange={handleEntityChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select an entity..." />
              </SelectTrigger>
              <SelectContent>
                {listableEntities.length > 0 ? (
                  listableEntities.map((entity) => (
                    <SelectItem key={entity.name} value={entity.name}>
                      {entity.label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 txt-small-regular text-text-tertiary">
                    No listable entities available
                  </div>
                )}
              </SelectContent>
            </Select>
          )}
          <p className="txt-xs-regular text-text-tertiary">
            The type of entity users can select or create
          </p>
        </div>

        {/* Display Field (Label) */}
        <div className="space-y-2">
          <Label>Display Field (Label)</Label>
          <SchemaFieldPicker
            entity={selectedEntity}
            value={(props?.displayField as string) || ''}
            onSelect={(path) => onUpdateProps('displayField', path)}
            placeholder="Select field for label..."
            readableOnly
          />
          <p className="txt-xs-regular text-text-tertiary">
            The field to display as the option label (e.g., legal name)
          </p>
        </div>

        {/* Value Field */}
        <div className="space-y-2">
          <Label>Value Field</Label>
          <SchemaFieldPicker
            entity={selectedEntity}
            value={(props?.valueField as string) || 'id'}
            onSelect={(path) => onUpdateProps('valueField', path)}
            placeholder="Select field for value..."
            readableOnly
          />
          <p className="txt-xs-regular text-text-tertiary">
            The field to store as the selected value (usually ID)
          </p>
        </div>

        {/* Selection Limits */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Min Selection</Label>
            <Input
              type="number"
              min={0}
              value={(props?.minSelection as number) ?? 1}
              onChange={(e) => {
                const minValue = parseInt(e.target.value, 10) || 0;
                onUpdateProps('minSelection', minValue);
                updateSelectionValidation('min', minValue);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Max Selection</Label>
            <Input
              type="number"
              min={0}
              value={(props?.maxSelection as number) ?? 1}
              onChange={(e) => {
                const maxValue = parseInt(e.target.value, 10) || 0;
                onUpdateProps('maxSelection', maxValue);
                updateSelectionValidation('max', maxValue);
              }}
            />
            <p className="txt-xs-regular text-text-tertiary">0 = unlimited</p>
          </div>
        </div>

        {/* Custom Labels */}
        <div className="space-y-2">
          <Label>Add New Label</Label>
          <Input
            value={(props?.addNewLabel as string) || ''}
            onChange={(e) => onUpdateProps('addNewLabel', e.target.value)}
            placeholder={`Add new ${selectedEntitySchema?.label || 'entity'}`}
          />
        </div>

        <div className="space-y-2">
          <Label>Select Placeholder</Label>
          <Input
            value={(props?.selectLabel as string) || ''}
            onChange={(e) => onUpdateProps('selectLabel', e.target.value)}
            placeholder={`Add or select ${selectedEntitySchema?.label || 'entity'}s`}
          />
        </div>

        {/* List Path (read-only, from schema) */}
        {selectedEntitySchema?.listConfig?.path && (
          <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
            <p className="txt-xs-regular text-text-tertiary">
              <Info className="inline-block w-3 h-3 mr-1" />
              Data will be fetched using path:{' '}
              <code className="bg-bg-tertiary px-1 py-0.5 rounded">
                {selectedEntitySchema.listConfig.path}
              </code>
            </p>
          </div>
        )}

        {/* Template Fields Section */}
        <div className="space-y-3 pt-4 border-t border-border-secondary">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="txt-small-medium text-text-secondary">
                Entity Fields
              </h4>
              <p className="txt-xs-regular text-text-tertiary">
                Fields to collect for each selected entity
              </p>
            </div>
            {requiredFields.length > 0 &&
              (!template || template.length === 0) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAutoPopulateRequiredFields}
                  disabled={isEntitySchemaLoading}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Auto-populate Required
                </Button>
              )}
          </div>

          {/* Required fields info */}
          {requiredFields.length > 0 && (
            <div className="p-3 bg-bg-warning-subtle rounded-md border border-border-warning">
              <p className="txt-xs-regular text-text-warning">
                <AlertCircle className="inline-block w-3 h-3 mr-1" />
                Required fields for creating a new{' '}
                {selectedEntitySchema?.label || 'entity'}:{' '}
                {requiredFields.map((f) => f.label).join(', ')}
              </p>
            </div>
          )}

          {/* Template Fields List */}
          {template && template.length > 0 ? (
            <div className="space-y-2">
              {template.map((field, index) => (
                <div
                  key={field.id}
                  className="p-3 bg-bg-secondary rounded-md border border-border-secondary"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="txt-small-medium text-text-secondary truncate">
                        {field.label || `Field ${index + 1}`}
                      </span>
                      {isKeyPersonEntity &&
                        field.dataSource?.commendaOS?.entity && (
                          <Badge variant="gray" className="text-xs shrink-0">
                            {entityToCategoryLabel(
                              field.dataSource.commendaOS.entity
                            )}
                          </Badge>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveTemplateField(field.id)}
                      className="p-1 hover:bg-bg-tertiary rounded shrink-0"
                    >
                      <Trash2 className="w-4 h-4 text-text-tertiary hover:text-text-error" />
                    </button>
                  </div>
                  {isKeyPersonEntity ? null : (
                    <div className="mt-2">
                      <SchemaFieldPicker
                        entity={selectedEntity}
                        value={field.dataSource?.commendaOS?.field?.read || ''}
                        onSelect={(path, label) => {
                          handleUpdateTemplateField(field.id, {
                            label: label || path,
                            fieldId: path.replace(/\./g, '-'),
                            dataSource: {
                              type: COMMENDA_OS_DATA_SOURCE,
                              commendaOS: {
                                entity: selectedEntity,
                                field: { read: path },
                                mode: 'read-write',
                                contextBindings: [],
                              },
                            },
                          });
                        }}
                        placeholder="Select field..."
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 border border-dashed border-border-secondary rounded-md text-center">
              <p className="txt-small-regular text-text-tertiary">
                No fields configured. Add fields to collect data for each
                entity.
              </p>
            </div>
          )}

          {/* Add Field: unified grouped dropdown for keyPerson, else plain button */}
          {isKeyPersonEntity && unifiedFieldMenu.groups.length > 0 ? (
            <Dropdown
              menu={unifiedFieldMenu}
              align="start"
              side="bottom"
              sideOffset={4}
              contentClassName="max-h-[300px] overflow-y-auto"
            >
              <Button variant="tertiary" size="sm" className="w-full">
                <Plus className="w-4 h-4 mr-1" />
                Add Field
              </Button>
            </Dropdown>
          ) : (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => handleAddTemplateField()}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Field
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
